<!-- SEO landing page (target: "scan receipts and organize" ~480/mo, KD ~18).
     Workflow-focused: snap → auto-extract → categorized archive → export. -->
<template>
  <SeoLanding
    kicker="Workflow"
    h1="Scan Receipts and Organize Them Automatically"
    subtitle="One workflow, four steps: snap the receipt, let AI extract merchant, date, line items, tax, and total, land it in a searchable archive, and export clean CSV, Excel, or JSON whenever you need it."
    cta-label="Scan your first receipt free"
    cta-to="/signup"
    :sections="sections"
    :faqs="faqs"
    closing-title="From paper pile to organized data"
    closing-subtitle="Every receipt you snap files itself. Free for 5 pages a month — no credit card required."
    :related-links="relatedLinks"
  />
</template>

<script setup>
import SeoLanding from '@/components/marketing/SeoLanding.vue'
import { usePageSeo, faqJsonLd, articleJsonLd, breadcrumbJsonLd } from '@/composables/usePageSeo'

const SITE = 'https://www.papersnap.io'
const CANONICAL = `${SITE}/scan-receipts-and-organize`

const sections = [
  {
    h2: 'Scanning is easy — organizing is the real job',
    paragraphs: [
      'Photographing a receipt takes two seconds. The work everyone dreads is what comes after: reading each photo, typing amounts into a spreadsheet, and filing the images somewhere findable. A camera-roll folder of 200 receipt photos is scanned, but it is not organized — you still cannot answer "how much did I spend on supplies in Q2?" without opening every image.',
      'The workflow below closes that gap by making extraction and filing automatic, so scanning is the only thing you do by hand.',
    ],
  },
  {
    h2: 'Step 1 — Snap or forward',
    bullets: [
      { label: 'Phone camera', text: 'open Papersnap in your phone browser (installable as a PWA) and photograph the receipt — angled shots and faded thermal ink are handled' },
      { label: 'Drag and drop', text: 'upload PDFs, PNGs, JPGs, or TIFFs from your desktop, including multi-page documents' },
      { label: 'Email-in', text: 'forward emailed receipts to your personal Papersnap address and they enter the same pipeline automatically' },
    ],
  },
  {
    h2: 'Step 2 — Auto-extraction does the typing',
    paragraphs: [
      'Seconds after upload, document AI classifies the document and extracts the structured fields: merchant, date, each line item, payment method, tax, and total. Every field carries a confidence score, and the parsed data is displayed side by side with the original image — so verifying a receipt is a glance, not a re-read. No templates to set up: it works on merchants and layouts it has never seen.',
      'On the Business plan and above you can add custom extraction fields per document type — a project code, client name, or cost center — and they are pulled from every scan automatically.',
    ],
  },
  {
    h2: 'Step 3 — A categorized, searchable archive',
    paragraphs: [
      'Each scanned receipt lands in your document history, classified by type and searchable with filters — so finding one purchase later takes seconds, not a scroll through camera-roll thumbnails. History retention runs from 30 days on the free plan up to 365 days on Enterprise, and on Enterprise a team of 5 shares one pooled archive and quota, so everyone scans into the same organized system.',
    ],
  },
  {
    h2: 'Step 4 — Export where the data needs to go',
    bullets: [
      { label: 'CSV', text: 'open in Google Sheets or import into accounting software' },
      { label: 'Excel (.xlsx)', text: 'a real labeled workbook — fields, line items, and tables on separate sheets' },
      { label: 'JSON', text: 'feed scripts, APIs, or your own tooling with structured data' },
      { label: 'Email', text: 'send extracted data straight to an inbox, or auto-send every result on Business+' },
    ],
    paragraphs: [
      'Because the export contains extracted fields rather than images, monthly bookkeeping becomes a sort-and-sum on a spreadsheet. Bulk export on Business+ pulls a whole batch at once.',
    ],
  },
]

const faqs = [
  {
    q: 'How do I scan receipts and organize them automatically?',
    a: 'Use a scanner that extracts data, not just images. With Papersnap: photograph or forward each receipt, AI extracts merchant, date, line items, tax, and total, the receipt files itself into a searchable history, and you export CSV, Excel, or JSON for bookkeeping. The only manual step is capture.',
  },
  {
    q: 'Can I scan multiple receipts at once?',
    a: 'Yes — upload multi-page PDFs or several images, and each document is classified and extracted independently. Plans are page-based: free covers 5 pages a month, Solo 200, Business 800, Enterprise 2,400 pooled across a team.',
  },
  {
    q: 'Does it work for invoices and other documents too?',
    a: 'Yes. The same pipeline reads invoices, purchase orders, utility bills, and financial reports, classifying each automatically — so one workflow covers all your paperwork, not just receipts.',
  },
  {
    q: 'What formats can I export scanned receipts in?',
    a: 'JSON, CSV, and real .xlsx Excel workbooks on every plan, including free. Business+ adds bulk export and can auto-email extracted data to a destination you choose.',
  },
  {
    q: 'Is my receipt data private?',
    a: 'Documents are stored in S3 with per-user isolation and auto-deleted at the end of your plan’s retention window (30–365 days by plan). Data is never sold or shared with advertisers; export your records anytime for your own long-term archive.',
  },
]

const relatedLinks = [
  { to: '/how-to-organize-receipts', label: 'How to organize receipts' },
  { to: '/receipt-scanner-app', label: 'Receipt scanner app' },
  { to: '/invoice-ocr', label: 'Invoice OCR' },
]

usePageSeo({
  title: 'Scan Receipts and Organize Them Automatically',
  description:
    'A four-step workflow: snap a receipt, AI extracts merchant, date, line items, tax, and total, everything files into a searchable archive, and exports as CSV, Excel, or JSON.',
  path: '/scan-receipts-and-organize',
  jsonLd: [
    articleJsonLd({
      headline: 'Scan Receipts and Organize Them Automatically',
      description: 'The snap → auto-extract → categorized archive → export workflow for receipts.',
      url: CANONICAL,
      datePublished: '2026-07-21',
    }),
    faqJsonLd(faqs.map((f) => ({ question: f.q, answer: f.a }))),
    breadcrumbJsonLd([
      { name: 'Home', url: `${SITE}/` },
      { name: 'Scan Receipts and Organize', url: CANONICAL },
    ]),
  ],
})
</script>
